import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum ReflorWorkOrderLineItemKeys {
  itemNumber = 'itemNumber',
  description = 'description',
  quantity = 'quantity',
  UOM = 'UOM',
  price = 'price',
  lineTotal = 'lineTotal',
}

const ReflorLineItemSchema = Yup.object().shape({
  [ReflorWorkOrderLineItemKeys.itemNumber]: Yup.number().nullable().optional(),
  [ReflorWorkOrderLineItemKeys.quantity]: Yup.string().nullable().optional(),
  [ReflorWorkOrderLineItemKeys.description]: Yup.string().nullable().optional(),
  [ReflorWorkOrderLineItemKeys.price]: Yup.string().nullable().optional(),
  [ReflorWorkOrderLineItemKeys.UOM]: Yup.string().nullable().optional(),
  [ReflorWorkOrderLineItemKeys.lineTotal]: Yup.string().nullable().optional(),
})

export const reflorWorkOrderFileDataSchema = Yup.object().shape({
  organizationLogoOverride: Yup.string().nullable().optional(),
  contact: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      email: Yup.string().nullable().optional(),
    })
    .required(),
  organization: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  vendor: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
    })
    .required(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  proposalNumber: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  deliveryDate: Yup.string().nullable().optional(),
  installDate: Yup.string().nullable().optional(),
  completionDate: Yup.string().nullable().optional(),
  site: Yup.object().shape({
    contactName: Yup.string().nullable().optional(),
    contactTitle: Yup.string().nullable().optional(),
    contactPhone: Yup.string().nullable().optional(),
  }),
  client: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      contactPhone: Yup.string().nullable().optional(),
    })
    .required(),
  note: Yup.string().nullable().optional(),
  items: Yup.array().of(ReflorLineItemSchema).required(),
})

export type ReflorWorkOrderFileData = Yup.InferType<typeof reflorWorkOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<ReflorWorkOrderFileData>

export const reflorWorkOrderFileMappings: RequiredFileData = {
  organizationLogoOverride: 'c2850410-233c-4f77-8ea8-69859013a6ef',
  contact: {
    name: 'e5ce7e05-72ad-48e6-947b-62163788e1a8',
    phoneNumber: '176a7128-14c9-477f-b7b7-54af030a28e2',
    email: '9e0d5878-49fa-496c-ac1e-cdd18ed87d98',
  },
  organization: {
    companyName: '3e767f5d-b00c-432d-9f3e-c40992dc9630',
    addressLine1: '49f559f4-7b7c-49da-b95e-d499bbd9a72c',
    addressLine2: 'a120f91c-82c9-426e-8676-397b21b0a233',
    cityStateZip: '042bd8b1-2123-4b04-8bc8-4ceb18989024',
    phoneNumber: 'c9634b60-15d8-4048-bd81-af540523226d',
    website: 'f868e9be-9697-48c4-8158-19e820397f0b',
  },
  vendor: {
    companyName: '03e9afaf-6e77-490c-8989-6f24318e7276',
    addressLine1: '96074f9b-e10f-47fa-9be9-f417fc8dba09',
    addressLine2: '66eeef1d-7b02-4e32-b007-ab66e9a53785',
    cityStateZip: '74187a10-a012-418d-807b-f8a686fafbfc',
  },
  purchaseOrderNumber: '7e19775c-7844-4d94-897d-204dbccaa9e2',
  proposalNumber: 'b7308582-d07c-474b-bd52-50a606530ea9',
  date: 'a1e053db-6eb2-4586-933c-1091760c6bfa',
  deliveryDate: '2ee7d158-6c2a-4900-a27e-74bc94437cc3',
  installDate: '6e389295-1519-4776-b762-0907a26a10df',
  completionDate: 'a7aefef6-4a7c-4179-a1ca-0dd5cc9d5c4d',
  site: {
    contactName: '7171b9ae-f622-4293-b76e-863daa61d419',
    contactTitle: 'd98126b4-b38c-4fad-abad-b0320f44b0c1',
    contactPhone: 'af6ecc3e-1811-405b-a66a-c4050e5af7f4',
  },
  client: {
    company: 'cb852c76-41f7-49a7-a8d6-0f73a0208956',
    addressLine1: 'd3bc5748-7871-49a1-80cf-d98b05d0c9f6',
    addressLine2: '7c125ed9-253b-4ac0-a9ba-d1f476e0b78a',
    cityStateZip: 'c6dc8733-e1dd-4923-a302-52172d505920',
    contactPhone: '545f77bf-f750-4299-9f6a-b747c4841bf4',
  },
  note: '7a3dc641-b444-4062-b134-3540c652cd87',
  items: [
    {
      itemNumber: null,
      quantity: '46c1daa4-949d-49da-8161-9cbc8943c628',
      description: '38b57dd9-5d6d-418e-8451-ee865f1e90df',
      price: '07f3a61c-d7a0-47ed-9c06-7e7db983cf5e',
      UOM: '4ca192ef-922b-42fa-a547-718f89497e3e',
      lineTotal: null,
    },
  ],
}
